import { Link, useFetcher, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@repo-manager/ui';

import { useRepos } from '../lib/repo-context';
import { getSupabase } from '../lib/supabase';

interface SprintData {
  sprint: {
    id: string;
    title: string;
    goal: string;
    status: string;
    start_date: string | null;
    end_date: string | null;
    repo_id: string;
  };
  rows: {
    id: string; // sprint_stories.id
    story_id: string;
    title: string;
    status: string;
    effort: string;
    sprint_story_status: string;
  }[];
}

const SPRINT_STORY_STATUSES = ['committed', 'in_progress', 'in_review', 'done', 'carried_over'] as const;

export async function sprintLoader({ params }: LoaderFunctionArgs): Promise<SprintData> {
  if (!params.id) throw new Response('Not Found', { status: 404 });
  const supabase = getSupabase();
  const { data: sprint, error } = await supabase
    .from('sprints')
    .select('id, title, goal, status, start_date, end_date, repo_id')
    .eq('id', params.id)
    .single();
  if (error || !sprint) throw new Response('Sprint not found', { status: 404 });
  const { data: ss } = await supabase
    .from('sprint_stories')
    .select('id, story_id, status, stories(id, title, status, effort)')
    .eq('sprint_id', sprint.id);
  const rows = (ss ?? [])
    .map((r) => {
      const story = r.stories as unknown as { id: string; title: string; status: string; effort: string } | null;
      if (!story) return null;
      return {
        id: r.id,
        story_id: r.story_id,
        title: story.title,
        status: story.status,
        effort: story.effort,
        sprint_story_status: r.status,
      };
    })
    .filter(Boolean) as SprintData['rows'];
  return { sprint, rows };
}

export async function sprintAction({ request }: LoaderFunctionArgs) {
  const fd = await request.formData();
  const op = fd.get('op');
  const supabase = getSupabase();
  if (op === 'set-status') {
    const id = String(fd.get('id'));
    const status = String(fd.get('status'));
    const { error } = await supabase.from('sprint_stories').update({ status }).eq('id', id);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }
  return null;
}

export function SprintPage() {
  const { sprint, rows } = useLoaderData() as SprintData;
  const { repos } = useRepos();
  const repo = repos.find((r) => r.id === sprint.repo_id) ?? null;
  const fetcher = useFetcher();

  useEffect(() => {
    const r = fetcher.data;
    if (r && typeof r === 'object' && 'ok' in r) {
      if ((r as { ok: boolean }).ok) toast.success('Updated');
      else toast.error('Update failed', { description: (r as { error?: string }).error });
    }
  }, [fetcher.data]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          {repo ? (
            <p className="text-sm text-muted-foreground font-mono">
              {repo.owner}/{repo.name}
            </p>
          ) : null}
          <h1 className="text-2xl font-semibold tracking-tight">{sprint.title}</h1>
          <div className="mt-1 flex items-center gap-2">
            <StatusBadge status={sprint.status} />
            <span className="text-sm text-muted-foreground">
              {sprint.start_date ?? '—'} → {sprint.end_date ?? '—'}
            </span>
          </div>
        </div>
        <Button asChild variant="ghost">
          <Link to="/sprints">← Sprints</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Goal</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap text-sm">{sprint.goal}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Stories ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-6">
              <EmptyState title="No stories on this sprint" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Story</TableHead>
                  <TableHead>Effort</TableHead>
                  <TableHead>Sprint status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.effort}</TableCell>
                    <TableCell>
                      <Select
                        defaultValue={r.sprint_story_status}
                        onValueChange={(status) => {
                          const fd = new FormData();
                          fd.set('op', 'set-status');
                          fd.set('id', r.id);
                          fd.set('status', status);
                          fetcher.submit(fd, { method: 'post' });
                        }}
                      >
                        <SelectTrigger className="w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SPRINT_STORY_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
